import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Check, Calculator } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Eyebrow, Reveal } from "../components/motion";
import { BOOKING } from "../data/content";
import { submitContact } from "../utils/api";

const eur = (n) => `${Math.round(n).toLocaleString("fr-FR")} €`;

const simulationMessage = (s) =>
  `Simulation réalisée sur le site : bien ${eur(s.prix)}, travaux ${eur(s.travaux)}, apport ${eur(s.apport)}, crédit ${s.taux} % sur ${s.duree} ans (mensualité ≈ ${eur(s.mensualite)}). Objectif : ${eur(s.prixNuit)}/nuit × ${s.nuits} nuits = ${eur(s.revenus)} de revenus, charges ${eur(s.charges)} → cash-flow estimé ≈ ${s.cashflow >= 0 ? "+" : "−"} ${eur(Math.abs(s.cashflow))}/mois.`;

const EMPTY = {
  prenom: "",
  nom: "",
  email: "",
  telephone: "",
  has_property: "",
  project_type: "",
  zone: "",
  stage: "",
  objective: "",
  message: "",
};

export default function RendezVous() {
  const simulation = useLocation().state?.simulation || null;
  const [form, setForm] = useState(simulation ? { ...EMPTY, message: simulationMessage(simulation) } : EMPTY);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const q = BOOKING.questions;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContact(simulation ? { ...form, simulation } : form);
      setDone(true);
      toast.success("Votre demande a bien été envoyée.");
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="page-rendez-vous">
      <Seo
        title="Prendre rendez-vous — Parlons de votre projet | Demoiselle Atypique"
        description="Vous souhaitez créer une Love Room, un hébergement atypique ou développer un concept immobilier différent ? Réservez un échange avec l'équipe Demoiselle Atypique."
      />

      <section className="px-6 pb-24 pt-36 sm:px-10 lg:px-16 lg:pt-44">
        <div className="mx-auto grid max-w-[1500px] gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow>Prise de rendez-vous</Eyebrow>
            <h1 className="mt-6 font-serif text-5xl leading-[1.06] text-cream sm:text-6xl">{BOOKING.title}</h1>
            <Reveal delay={0.1}>
              <p className="mt-8 leading-relaxed text-white/60">{BOOKING.intro}</p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-4 leading-relaxed text-white/60">{BOOKING.intro2}</p>
            </Reveal>
            <Reveal delay={0.22}>
              <ul className="mt-12 space-y-4" data-testid="rendez-vous-points">
                {[
                  "Un échange direct avec notre équipe",
                  "Une première lecture de votre projet et de son potentiel",
                  "Des prochaines étapes claires pour la suite",
                  "Un premier échange sans engagement",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-white/70">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" /> {point}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {BOOKING.externalCalendarUrl ? (
              <iframe
                src={BOOKING.externalCalendarUrl}
                title="Calendrier de réservation externe"
                data-testid="rendez-vous-external-calendar"
                className="min-h-[640px] w-full border border-white/10 bg-[#1D1327]"
              />
            ) : done ? (
              <div className="flex h-full flex-col items-start justify-center gap-5 border border-neon/40 bg-[#1D1327] p-10 lg:p-14" data-testid="rendez-vous-success">
                <span className="grid h-14 w-14 place-items-center rounded-full border border-neon text-neon">
                  <Check className="h-6 w-6" />
                </span>
                <p className="font-serif text-4xl text-cream">Merci, c'est bien reçu.</p>
                <p className="max-w-md leading-relaxed text-white/60">
                  Votre demande de rendez-vous nous est parvenue avec les informations sur votre projet. Notre équipe
                  revient vers vous très vite pour convenir de l'échange.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="rendez-vous-form" className="border border-white/10 bg-[#1D1327] p-8 lg:p-12">
                {simulation && (
                  <div className="mb-8 border border-neon/30 bg-neon/[0.05] p-5" data-testid="rendez-vous-simulation">
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neon">
                      <Calculator className="h-3.5 w-3.5" /> Votre simulation est jointe à la demande
                    </p>
                    <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                      {[
                        ["Projet", eur(simulation.total)],
                        ["Mensualité", eur(simulation.mensualite)],
                        ["Revenus / mois", eur(simulation.revenus)],
                        ["Cash-flow estimé", `${simulation.cashflow >= 0 ? "+ " : "− "}${eur(Math.abs(simulation.cashflow))}`],
                      ].map(([l, v]) => (
                        <div key={l}>
                          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">{l}</p>
                          <p className="mt-1 font-serif text-xl text-cream">{v}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon">Votre projet</p>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="rdv-bien" className="field-label">{q.has_property.label}</label>
                    <select id="rdv-bien" data-testid="rendez-vous-input-bien" className="field" required value={form.has_property} onChange={set("has_property")}>
                      <option value="" disabled>Sélectionner</option>
                      {q.has_property.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="rdv-type" className="field-label">{q.project_type.label}</label>
                    <select id="rdv-type" data-testid="rendez-vous-input-type" className="field" required value={form.project_type} onChange={set("project_type")}>
                      <option value="" disabled>Sélectionner</option>
                      {q.project_type.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="rdv-zone" className="field-label">{q.zone.label}</label>
                    <input id="rdv-zone" data-testid="rendez-vous-input-zone" className="field" required value={form.zone} onChange={set("zone")} placeholder={q.zone.placeholder} />
                  </div>
                  <div>
                    <label htmlFor="rdv-etape" className="field-label">{q.stage.label}</label>
                    <select id="rdv-etape" data-testid="rendez-vous-input-etape" className="field" required value={form.stage} onChange={set("stage")}>
                      <option value="" disabled>Sélectionner</option>
                      {q.stage.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="rdv-objectif" className="field-label">{q.objective.label}</label>
                    <select id="rdv-objectif" data-testid="rendez-vous-input-objectif" className="field" required value={form.objective} onChange={set("objective")}>
                      <option value="" disabled>Sélectionner</option>
                      {q.objective.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="rdv-message" className="field-label">Un mot sur votre projet (optionnel)</label>
                    <textarea id="rdv-message" data-testid="rendez-vous-input-message" rows={3} className="field" value={form.message} onChange={set("message")} placeholder="Décrivez votre idée en quelques lignes..." />
                  </div>
                </div>

                <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-neon">Vos coordonnées</p>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="rdv-prenom" className="field-label">Prénom</label>
                    <input id="rdv-prenom" data-testid="rendez-vous-input-prenom" className="field" required value={form.prenom} onChange={set("prenom")} placeholder="Camille" />
                  </div>
                  <div>
                    <label htmlFor="rdv-nom" className="field-label">Nom</label>
                    <input id="rdv-nom" data-testid="rendez-vous-input-nom" className="field" required value={form.nom} onChange={set("nom")} placeholder="Durand" />
                  </div>
                  <div>
                    <label htmlFor="rdv-email" className="field-label">E-mail</label>
                    <input id="rdv-email" data-testid="rendez-vous-input-email" type="email" className="field" required value={form.email} onChange={set("email")} placeholder="camille@exemple.fr" />
                  </div>
                  <div>
                    <label htmlFor="rdv-tel" className="field-label">Téléphone</label>
                    <input id="rdv-tel" data-testid="rendez-vous-input-telephone" type="tel" className="field" required value={form.telephone} onChange={set("telephone")} placeholder="06 12 34 56 78" />
                  </div>
                </div>

                <button type="submit" disabled={sending} data-testid="rendez-vous-submit" className="btn-primary mt-10 w-full disabled:opacity-60">
                  {sending ? "Envoi..." : BOOKING.cta} <ArrowRight className="h-4 w-4" />
                </button>
                <p className="mt-4 text-xs leading-relaxed text-white/35">
                  Vos informations servent uniquement à organiser notre échange. Consultez notre politique de confidentialité pour en savoir plus sur le traitement de vos données.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
